import { Box, styled } from "@mui/material";
import StoreRadio from "../../../StoreComponents/StoreRadio";
import {
  AsyncCheckoutOrderType,
  ShippingGroup,
  ShoppingCartItem,
} from "../../types";
import { SummaryProduct } from "../SummaryProductV2";
import { StoreAnimatedHeightDiv } from "../../../StoreComponents/StoreAnimatedHeightDiv";
import { AnimatePresence, motion } from "motion/react";
import KeyboardArrowRightRounded from "@mui/icons-material/KeyboardArrowRightRounded";
import {
  MOBILE_SCREEN_SIZE,
  TABLET_SCREEN_SIZE,
} from "../../../AppNavigation/constants";

interface ShippingGroupPreviewProps {
  orderType?: AsyncCheckoutOrderType;
  shippingGroup: ShippingGroup;
  deliveryOpen: boolean[];
  index: number;
  retailerOrderId?: string;
  retailerOrderPageUrl?: string;
  openItem: (index: number) => void;
  handleUpdateShippingOption: (
    deliveryOption?:
      | {
          name: string;
          cssSelector: string;
        }
      | undefined,
    deliveryTimeWindow?:
      | {
          name: string;
          cssSelector: string;
        }
      | undefined
  ) => Promise<void>;
  items?: ShoppingCartItem[];
}

const Container = styled(Box)({
  padding: "0px 0",
  width: "100%",
  display: "flex",
  flexDirection: "column",
  gap: 16,

  ".selected_title": {
    fontSize: 16,
    fontWeight: 500,

    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    [`@media screen and (max-width: ${TABLET_SCREEN_SIZE}px)`]: {
      fontSize: 14,
    },
    [`@media screen and (max-width: ${MOBILE_SCREEN_SIZE}px)`]: {
      fontSize: 14,
    },
  },
  ".product_list": {
    display: "flex",
    flexDirection: "column",
    gap: 15,
    maxWidth: 420,
  },
  ".link": {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    fontSize: 14,
    fontWeight: 700,
    color: "inherit",
    textDecoration: "none",
  },
  ".shipping_options": {
    display: "flex",
    flexDirection: "column",
    gap: "16px",
    maxWidth: 600,
    ".header": {
      fontSize: 16,
      fontWeight: 600,
    },
    ".single_option": {
      ".option_title": {
        fontSize: 14,
      },
      ".window_list": {
        paddingLeft: 28,
        paddingTop: 12,
        display: "flex",
        flexDirection: "row",
        gap: "8px",
        flexWrap: "wrap",
      },
    },
  },
});

const ShippingOption = ({
  option,
  updateOrderShippingOption,
}: {
  option: ShippingGroup["deliveryOptions"][0];
  updateOrderShippingOption: (
    deliveryOption?:
      | {
          name: string;
          cssSelector: string;
        }
      | undefined,
    deliveryTimeWindow?:
      | {
          name: string;
          cssSelector: string;
        }
      | undefined
  ) => Promise<void>;
}) => {
  if (!option.deliveryTimeWindows || option.deliveryTimeWindows.length === 0) {
    return (
      <Box className="single_option">
        <Box
          display="flex"
          flexDirection="row"
          justifyContent="space-between"
          width="100%"
        >
          <Box display="flex" flexDirection="row" alignItems="center">
            <StoreRadio
              key={option.name + option.cssSelector}
              checked={!!option.isSelected}
              disableRipple
              onClick={() => {
                updateOrderShippingOption({
                  name: option.name,
                  cssSelector: option.cssSelector,
                });
              }}
            />
            <Box
              marginLeft={1}
              className="option_title"
              sx={{
                fontWight: option.isSelected ? 600 : 500,
              }}
            >
              {option.name} - {option.description}
            </Box>
          </Box>
        </Box>
      </Box>
    );
  } else {
    return (
      <>
        {option.deliveryTimeWindows
          .filter((time) => {
            return time?.name || time?.description;
          })
          .map((e) => {
            return (
              <Box className="single_option" key={e.name + e.cssSelector}>
                <Box
                  display="flex"
                  flexDirection="row"
                  justifyContent="space-between"
                  width="100%"
                >
                  <Box display="flex" flexDirection="row" alignItems="center">
                    <StoreRadio
                      key={e.name + e.cssSelector}
                      checked={!!e.isSelected}
                      disableRipple
                      onClick={() => {
                        updateOrderShippingOption(undefined, {
                          name: e.name,
                          cssSelector: e.cssSelector,
                        });
                      }}
                    />
                    <Box marginLeft={1} className="option_title">
                      {e.name} - {option.description}
                    </Box>
                  </Box>
                </Box>
              </Box>
            );
          })}
      </>
    );
  }
};

export const ShippingGroupPreview = ({
  shippingGroup,
  index,
  handleUpdateShippingOption,
  deliveryOpen,
  retailerOrderId,
  retailerOrderPageUrl,
  items,
  orderType,
}: ShippingGroupPreviewProps) => {
  const selectedOption = shippingGroup.deliveryOptions.find(
    (e) => e.isSelected
  );
  const selectedTimeWindow = selectedOption?.deliveryTimeWindows?.find(
    (e) => e.isSelected
  );

  return (
    <>
      <Container className="shipping_group">
        <div className="selected_title">
          {retailerOrderId &&
          retailerOrderPageUrl &&
          orderType !== AsyncCheckoutOrderType.GUEST ? (
            <>
              <span style={{ textDecoration: "underline" }}>
                <a
                  href={retailerOrderPageUrl}
                  rel="noreferrer"
                  target="_blank"
                  style={{
                    textDecoration: "none",
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "center",
                    color: "inherit",
                  }}
                >
                  {`#${retailerOrderId}`}
                </a>
              </span>

              <a
                href={retailerOrderPageUrl}
                rel="noreferrer"
                target="_blank"
                style={{
                  textDecoration: "none",
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                  color: "inherit",
                }}
                onClick={(e) => {
                  e.stopPropagation();
                }}
              >
                <div className="">Track package</div>

                <KeyboardArrowRightRounded
                  style={{ color: "inherit", fontSize: 21, marginRight: -3 }}
                />
              </a>
            </>
          ) : (
            <span style={{ textDecoration: "underline" }}>
              {selectedOption?.deliveryTimeWindows &&
              selectedOption.deliveryTimeWindows.length >= 1
                ? `${selectedTimeWindow?.name} ${
                    selectedOption?.description ? "-" : ""
                  } ${selectedOption?.description ?? ""}`
                : `${selectedOption?.name} ${
                    selectedOption?.description ? "-" : ""
                  } ${selectedOption?.description ?? ""}`}
            </span>
          )}
        </div>
        <div className="product_list">
          {shippingGroup.products.map((e, index) => {
            return (
              <SummaryProduct
                key={index}
                product={e as any}
                quantityToUse={
                  items?.find((item) => item.stacklineSku === e.stacklineSku)
                    ?.quantity
                }
              />
            );
          })}
        </div>

        {deliveryOpen[index] ? (
          <StoreAnimatedHeightDiv>
            <AnimatePresence mode="wait">
              <motion.div
                layout="position"
                key={deliveryOpen[index] ? "view" : "edit"}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.1 }}
              >
                <Box className="shipping_options">
                  {shippingGroup.deliveryOptions.map((e) => {
                    if (!e.name && !e.description) {
                      return null;
                    }

                    return (
                      <ShippingOption
                        key={e.name + e.description}
                        option={e}
                        updateOrderShippingOption={handleUpdateShippingOption}
                      />
                    );
                  })}
                </Box>
              </motion.div>
            </AnimatePresence>
          </StoreAnimatedHeightDiv>
        ) : null}
      </Container>
    </>
  );
};
